import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import DmyDateInput from "../../components/DmyDateInput";
import { formatDateDMY } from "../../lib/dates";
import { paymentSucceeded } from "../staff/staffShared";

function headers() {
  const token = localStorage.getItem("customer_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function PortalShell({ title, children, roleLabel }) {
  return (
    <div className="min-h-screen bg-[#fdfcfb]">
      <header className="bg-[#311b52] text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
          <Link
            to="/"
            className="flex flex-col items-start justify-center shrink-0"
            style={{ minWidth: '150px' }}
          >
            <img
              src={config.LOGO_URL}
              alt="BookMyJab"
              style={{
                width: '140px',
                height: 'auto',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <span
              className="text-[8px] md:text-[9px] italic font-medium text-white/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Uncompromising Care. Seamlessly Yours.
            </span>
          </Link>
          <div>
            <p className="text-xs opacity-70">{roleLabel}</p>
            <h1 className="font-semibold text-lg" style={{ fontFamily: "Poppins" }}>{title}</h1>
          </div>
          <div className="flex gap-3 text-sm sm:mt-0">
            <Link to="/" className="opacity-80">Home</Link>
            <Link to="/login" className="opacity-80">Switch</Link>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">{children}</main>
    </div>
  );
}

export function NursePortal() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [completeFor, setCompleteFor] = useState(null);
  const [completeForm, setCompleteForm] = useState({ batch_number: "", expiry_date: "", adverse_event: false, notes: "" });

  const load = () => axios.get(`${config.API}/nurse/today`, { headers: headers() })
    .then(({ data }) => setItems(data.items || []))
    .catch(() => navigate("/login"));

  useEffect(() => {
    if (!localStorage.getItem("customer_token")) navigate("/login");
    load();
  }, [navigate]);

  const act = async (id, action) => {
    setMsg("");
    try {
      await axios.post(`${config.API}/nurse/bookings/${id}/${action}`, {}, { headers: headers() });
      setMsg(`${action} recorded`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed");
    }
  };

  const submitComplete = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${config.API}/nurse/bookings/${completeFor}/complete`, completeForm, { headers: headers() });
      setCompleteFor(null);
      setMsg("Visit completed");
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Complete failed");
    }
  };

  return (
    <PortalShell title="Today's assignments" roleLabel="Nurse portal">
      {msg && <p className="text-sm text-[#673c96]">{msg}</p>}
      {items.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-[#673c96]/25 p-8 text-center text-[#673c96]">
          No assignments for today. Ops will assign confirmed bookings to your nurse ID.
        </div>
      )}
      {items.map((b) => (
        <div key={b.id} className="bg-white rounded-2xl p-5 border border-[#673c96]/10" data-testid="nurse-assignment-card">
          <div className="flex justify-between gap-2">
            <h2 className="font-semibold text-[#311b52]">{b.beneficiary_name || b.customer_name}</h2>
            <span className="text-xs text-[#f37721] font-semibold">{b.status}</span>
          </div>
          <p className="text-sm text-[#673c96] mt-1">{b.service_name || b.vaccine_name}</p>
          <p className="text-sm text-[#673c96]">{formatDateDMY(b.preferred_date)} · {b.preferred_slot}</p>
          <p className="text-xs text-[#673c96] mt-1">{b.address}</p>
          {(b.prescription_status || b.prescription_file_reference) && (
            <p className="text-xs text-[#673c96] mt-2">
              Rx: {b.prescription_status || "—"}
              {b.prescription_note ? ` · ${b.prescription_note}` : ""}
              {b.prescription_file_reference ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="text-[#f37721] font-semibold underline"
                    onClick={() => {
                      axios.get(`${config.API}/nurse/bookings/${b.id}/prescription`, { headers: headers(), responseType: "blob" })
                        .then((res) => {
                          const url = URL.createObjectURL(res.data);
                          window.open(url, "_blank");
                        })
                        .catch(() => setMsg("Could not open prescription"));
                    }}
                  >
                    View file
                  </button>
                </>
              ) : null}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <button className="px-3 py-1.5 rounded-full bg-[#f37721] text-white" onClick={() => act(b.id, "start")}>Start</button>
            <button className="px-3 py-1.5 rounded-full bg-[#673c96] text-white" onClick={() => act(b.id, "arrive")}>Arrived</button>
            <button className="px-3 py-1.5 rounded-full bg-emerald-700 text-white" onClick={() => { setCompleteFor(b.id); setCompleteForm({ batch_number: "", expiry_date: "", adverse_event: false, notes: "" }); }}>Complete</button>
          </div>
        </div>
      ))}

      {completeFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCompleteFor(null)}>
          <form onSubmit={submitComplete} className="bg-white rounded-3xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-[#311b52]">Complete visit</h3>
            <input required value={completeForm.batch_number} onChange={(e) => setCompleteForm({ ...completeForm, batch_number: e.target.value })} placeholder="Batch number" className="w-full border rounded-xl px-4 py-3" />
            <DmyDateInput required min={new Date().toLocaleDateString("en-CA")} value={completeForm.expiry_date} onChange={(iso) => setCompleteForm({ ...completeForm, expiry_date: iso })} className="w-full border rounded-xl px-4 py-3" />
            <label className="flex gap-2 text-sm text-[#311b52]">
              <input type="checkbox" checked={completeForm.adverse_event} onChange={(e) => setCompleteForm({ ...completeForm, adverse_event: e.target.checked })} />
              Adverse event observed
            </label>
            <textarea value={completeForm.notes} onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })} placeholder="Notes" className="w-full border rounded-xl px-4 py-3" rows={2} />
            <button className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold">Submit completion</button>
          </form>
        </div>
      )}
    </PortalShell>
  );
}

export function DoctorDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState({ upcoming: [], stats: {} });
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [dash, list] = await Promise.all([
        axios.get(`${config.API}/doctor/dashboard`, { headers: headers() }),
        axios.get(`${config.API}/doctor/bookings`, { headers: headers() }),
      ]);
      setData(dash.data);
      setItems((list.data.items || dash.data.upcoming || []).filter((b) => paymentSucceeded(b.payment_status)));
    } catch {
      navigate("/login");
    }
  };

  useEffect(() => { load(); }, [navigate]);

  const confirm = async (id) => {
    try {
      await axios.post(`${config.API}/doctor/bookings/${id}/confirm`, {}, { headers: headers() });
      setMsg("Booking confirmed");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Confirm failed");
    }
  };

  return (
    <PortalShell title="Doctor dashboard" roleLabel="Clinical review">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-xs text-[#673c96]">Upcoming</p>
          <p className="text-2xl font-bold text-[#311b52]">{data.stats?.upcoming ?? items.length}</p>
        </div>
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-xs text-[#673c96]">Completed</p>
          <p className="text-2xl font-bold text-[#311b52]">{data.stats?.completed_today ?? 0}</p>
        </div>
      </div>
      {msg && <p className="text-sm text-[#673c96]">{msg}</p>}
      {items.length === 0 && <p className="text-[#673c96]">No doctor visits queued.</p>}
      {items.map((b) => (
        <div key={b.id} className="bg-white rounded-2xl p-4 border border-[#673c96]/10">
          <div className="flex justify-between gap-2">
            <p className="font-semibold text-[#311b52]">{b.beneficiary_name || b.customer_name}</p>
            <span className="text-xs text-[#f37721]">{b.status}</span>
          </div>
          <p className="text-sm text-[#673c96]">{b.service_name || b.vaccine_name} · {formatDateDMY(b.preferred_date)}</p>
          <p className="text-xs text-[#673c96] mt-1">Rx: {b.prescription_status || "—"} {b.prescription_note ? `· ${b.prescription_note}` : ""}</p>
          {b.prescription_file_reference && (
            <button
              type="button"
              className="mt-2 text-xs font-semibold text-[#f37721] underline"
              onClick={() => {
                axios.get(`${config.API}/doctor/bookings/${b.id}/prescription`, { headers: headers(), responseType: "blob" })
                  .then((res) => {
                    const url = URL.createObjectURL(res.data);
                    window.open(url, "_blank");
                  })
                  .catch(() => setMsg("Could not open prescription"));
              }}
            >
              View prescription file
            </button>
          )}
          {["pending_confirmation", "Doctor Review Needed", "confirmed"].includes(b.status) && b.status !== "confirmed" && (
            <button type="button" onClick={() => confirm(b.id)} className="mt-3 px-4 py-1.5 rounded-full bg-[#311b52] text-white text-sm font-semibold">Confirm visit</button>
          )}
        </div>
      ))}
    </PortalShell>
  );
}

export function HospitalDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState({ bookings: [], inventory: [], stats: {} });
  const [msg, setMsg] = useState("");

  const load = () => axios.get(`${config.API}/hospital/dashboard`, { headers: headers() })
    .then(({ data: d }) => setData(d))
    .catch(() => navigate("/login"));

  useEffect(() => { load(); }, [navigate]);

  const confirm = async (id) => {
    try {
      await axios.post(`${config.API}/hospital/bookings/${id}/confirm`, {}, { headers: headers() });
      setMsg("Confirmed");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <PortalShell title="Hospital dashboard" roleLabel="Partner hospital">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border p-3">
          <p className="text-[10px] uppercase text-[#673c96]">Total</p>
          <p className="text-xl font-bold text-[#311b52]">{data.stats?.total ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl border p-3">
          <p className="text-[10px] uppercase text-[#673c96]">Pending</p>
          <p className="text-xl font-bold text-[#311b52]">{data.stats?.pending ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl border p-3">
          <p className="text-[10px] uppercase text-[#673c96]">Inventory</p>
          <p className="text-xl font-bold text-[#311b52]">{data.stats?.inventory_skus ?? 0}</p>
        </div>
      </div>
      {msg && <p className="text-sm text-[#673c96]">{msg}</p>}

      <h2 className="font-semibold text-[#311b52] pt-2">Bookings</h2>
      {(data.bookings || []).slice(0, 30).map((b) => (
        <div key={b.id} className="bg-white rounded-2xl p-4 border">
          <div className="flex justify-between">
            <p className="font-semibold text-[#311b52]">{b.service_name || b.vaccine_name}</p>
            <span className="text-xs text-[#f37721]">{b.status}</span>
          </div>
          <p className="text-sm text-[#673c96]">{formatDateDMY(b.preferred_date)} · {b.beneficiary_name}</p>
          {b.status === "pending_confirmation" && (
            <button type="button" onClick={() => confirm(b.id)} className="mt-2 text-sm font-semibold text-[#311b52] underline">Confirm</button>
          )}
        </div>
      ))}

      {(data.inventory || []).length > 0 && (
        <>
          <h2 className="font-semibold text-[#311b52] pt-4">Inventory snapshot</h2>
          {(data.inventory || []).map((i) => (
            <div key={i.id || i.name} className="bg-white rounded-xl border px-4 py-3 text-sm flex justify-between">
              <span className="text-[#311b52]">{i.name}</span>
              <span className="text-[#673c96]">qty {i.quantity} · {i.batch_number || "—"}</span>
            </div>
          ))}
        </>
      )}
    </PortalShell>
  );
}
